import { useContext, useEffect, useState } from "react";
import {
  Button, Container, Grid, InputBase, Stack, IconButton, Typography, Select,
  Box, MenuItem, Table, TableHead, TableRow, TableBody, TableContainer, Paper,
  Pagination, Backdrop, CircularProgress, InputLabel, FormControl, Divider,
  TextField, InputAdornment,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import axios, { AxiosInstance } from "axios";
import {
  IPays, StyledTableCell, tableStyle
} from "../Pays/Pays";
import BuRow from "./BuRow";
import Cookies from "universal-cookie";
import OptionalWidthModal from "../../../../commons/optional-width-modal/OptionalWidthModal";
import BusinessUnitForm from "../../../forms/Bu/BusinessUnitForm";
import { AlertType } from "../../../../types/AlertType";
import { SnackBarAlert } from "../../../element/SnackBarAlert";
import CheckAttr from "../../../../utils/CheckAttr";
import ClearIcon from "@mui/icons-material/Clear";
import { loggoutUser } from "../../../../redux/actions/logginUser";
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { MediaQueryContext } from "../../../../utils/contextes/MediaQueryContext";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;

export const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

export interface IBu {
  id: number;
  libelle: string;
  created_at: string;
  updated_at: string;
  date_suppression: any;
  pays: IPays[];
}

const urlApi = process.env.REACT_APP_BASE_API_URL as string;

const Bu = () => {
  const { isDesktopOrLaptop } = useContext(MediaQueryContext);

  const cookies = new Cookies();
  const token: string = cookies.get("jwt");

  const http: AxiosInstance = axios.create({
    baseURL: urlApi,
    // timeout: 1000,
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [allPays, setAllPays] = useState<IPays[]>([]);
  const [dataBus, setDataBus] = useState<IBu[]>([]);
  const [libelleSearch, setLibelleSearch] = useState<string>("");
  const [loader, setLoader] = useState<boolean>(false);
  const [rowPerPage, setRowPerPage] = useState(5);
  const [page, setPage] = useState<number>(1);
  const [openSnack, setOpenSnack] = useState(false);
  const [refresh, setRefresh] = useState<boolean>(false);
  const [nb, setNb] = useState(0);
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [expanded, setExpanded] = useState<number | false>(false);

  const [alertMessage, setAlerteMessage] = useState<string>('');
  const [alertType, setAlertType] = useState<AlertType>("success");

  // Add global response interceptor
  http.interceptors.response.use(
    (response) => {
      // Modify response data here, if needed
      return response;
    },
    (error) => {
      console.log(error.message);
      if (error.message === "Request failed with status code 401") {
        // Remove the redux store
        dispatch(loggoutUser());

        // Remove token in navigator cookies
        cookies.remove('jwt', { path: '/' });

        navigate("/sign-in");
      }
      return Promise.reject(error);
    }
  );


  const getDataBus = () => {
    setLoader(true);
    const data = {
      rowPerPage: rowPerPage,
      page: page,
      libelle: libelleSearch,
    };

    http.post('get/bu', data)
      .then((res) => {
        setDataBus(res.data.data);
        setNb(res.data.total);
      }).catch(error => {
        console.log(error);
      }).finally(() => {
        setLoader(false);
      });
  };

  useEffect(() => {
    getDataBus();
  }, [page, rowPerPage, refresh]);

  const getAllPays = () => {
    const data = {};
    http.post('get/pays', data)
      .then((res) => {
        setAllPays(res.data.data);
      }).catch(error => {
        console.log(error);
      });
  };

  useEffect(() => {
    getAllPays();
  }, []);

  const ClickSearch = () => {
    setPage(1);
    getDataBus();
  };

  const handleChangeSearch = (e: any) => {
    const { value } = e.target;
    setLibelleSearch(e.target.value);

    if (!CheckAttr(value)) {
      reloadList();
    }
  };

  const handleChangeRowPerPage = (e: any) => {
    setPage(1);
    setRowPerPage(e.target.value);
  }

  const handleCloseModal = () => {
    setOpenModal(false);
  }

  const handleCloseAlert = () => {
    setOpenSnack(false);
  }

  const reloadList = () => {
    setLibelleSearch('');
    setPage(1);
    setRefresh(!refresh);
  };

  const pagination: JSX.Element | boolean = !loader &&
    <Stack spacing={2} direction="row">
      <InputLabel sx={{ color: "#fff" }}>Lignes par page :</InputLabel>
      <FormControl variant="standard" sx={{ minWidth: 50 }}>
        <Select
          labelId="demo-simple-select-standard-label"
          id="demo-simple-select-standard"
          color="warning"
          sx={{ background: "#fff", borderRadius: 1, paddingLeft: 1 }}
          value={rowPerPage}
          onChange={handleChangeRowPerPage}
          defaultValue={5}
        >
          <MenuItem value={5}>5</MenuItem>
          <MenuItem value={10}>10</MenuItem>
          <MenuItem value={25}>25</MenuItem>
          <MenuItem value={50}>50</MenuItem>
        </Select>
      </FormControl>
      {nb > rowPerPage && (
        <Pagination
          count={Math.ceil(nb / rowPerPage)}
          color="standard"
          sx={{ button: { color: "white" } }}
          showFirstButton={true}
          showLastButton={true}
          siblingCount={0}
          boundaryCount={1}
          defaultPage={page}
          onChange={(event, value) => setPage(value)}
        />)}
    </Stack>;

  const stackAlert: JSX.Element = SnackBarAlert(openSnack, handleCloseAlert, alertType, alertMessage);

  return (
    <>
      <Container maxWidth="lg">
        {isDesktopOrLaptop ? (
          <>
            <Grid container columns={{ xs: 6, sm: 12, md: 12 }}>
              <Grid item xs={2} sm={7} md={9}>
                <Button
                  variant="contained"
                  color="warning"
                  startIcon={<AddIcon />}
                  onClick={() => setOpenModal(true)}
                  style={{ textTransform: "none" }}
                >
                  Ajouter
                </Button>
              </Grid>
              <Grid item xs={4} sm={5} md={3}>
                <Stack
                  direction="row"
                  spacing={3}
                  display="flex"
                  justifyContent="end"
                >
                  <InputBase
                    sx={{ ml: 4, flex: 1, color: "white" }}
                    value={libelleSearch}
                    placeholder="Chercher un Bu"
                    onChange={handleChangeSearch}
                    fullWidth
                  />
                  <IconButton
                    type="submit"
                    sx={{ p: "10px", color: "white" }}
                    aria-label="search"
                    onClick={() => ClickSearch()}
                  >
                    <SearchIcon />
                  </IconButton>
                </Stack>
              </Grid>
            </Grid>

            <TableContainer component={Paper} style={tableStyle} sx={{ mt: 1 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <StyledTableCell align="center">
                      Bu
                    </StyledTableCell>
                    <StyledTableCell align="center">
                      Pays
                    </StyledTableCell>
                    <StyledTableCell align="center">
                      Action
                    </StyledTableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {dataBus.map((row) => (
                    <BuRow
                      key={row?.id}
                      row={row}
                      isDesktopOrLaptop={isDesktopOrLaptop}
                      allPays={allPays}
                      http={http}
                      expanded={expanded}
                      setExpanded={setExpanded}
                      reloadList={reloadList}
                      setOpenSnack={setOpenSnack}
                      setAlerteMessage={setAlerteMessage}
                      setAlertType={setAlertType}
                    />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Box
              mt={4}
              display="flex"
              justifyContent="right"
            >
              {pagination}
            </Box>
          </>
        ) : (
          <>
            <Box sx={{
              position: 'fixed',
              width: '95%',
              top: '10.32rem',
              pr: 1.5,
              pt: 2,
              bgcolor: '#080d24',
              zIndex: 1000
            }}>
              <Button
                variant="contained"
                size='small'
                startIcon={<AddIcon />}
                color="warning"
                fullWidth
                sx={{
                  display: { xs: "flex", sm: "none" },
                  textTransform: "none", mb: 1
                }}
                onClick={() => setOpenModal(true)}
              >
                Ajouter
              </Button>

              <TextField
                id="outlined-basic"
                variant="outlined"
                sx={{
                  bgcolor: "#fff",
                  borderRadius: 1
                }}
                placeholder="Chercher via un Bu..."
                size="small"
                fullWidth
                value={libelleSearch}
                onChange={handleChangeSearch}
                color="warning"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        type="button"
                        aria-label="search"
                        onClick={() => ClickSearch()}
                      >
                        <SearchIcon />
                      </IconButton>
                      <IconButton
                        type="button"
                        aria-label="refresh"
                        onClick={() => reloadList()}
                      >
                        <ClearIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Typography sx={{ mt: 3 }}>Liste Business units</Typography>
              <Divider orientation="horizontal" sx={{ bgcolor: "#fff", mb: 2 }} />

              <Box display="flex" justifyContent="end">
                <Pagination
                  count={Math.ceil(nb / rowPerPage)}
                  size="small"
                  color="standard"
                  sx={{ button: { color: "white" }, mb: 2 }}
                  showFirstButton={true}
                  showLastButton={true}
                  siblingCount={0}
                  boundaryCount={1}
                  defaultPage={page}
                  onChange={(event, value) => setPage(value)}
                />
              </Box>
            </Box>

            <Box mt={25}>
              {dataBus.map((row) => (
                <BuRow
                  key={row?.id}
                  row={row}
                  isDesktopOrLaptop={isDesktopOrLaptop}
                  allPays={allPays}
                  http={http}
                  expanded={expanded}
                  setExpanded={setExpanded}
                  reloadList={reloadList}
                  setOpenSnack={setOpenSnack}
                  setAlerteMessage={setAlerteMessage}
                  setAlertType={setAlertType}
                />
              ))}
            </Box>

            <Stack direction="row" sx={{ mt: 3, mb: 4 }}>
              <InputLabel sx={{ color: "#fff", fontSize: 12, mt: 0.5 }}>Lignes par page :</InputLabel>
              <FormControl variant="standard" sx={{ minWidth: 48 }}>
                <Select
                  labelId="demo-simple-select-standard-label"
                  id="demo-simple-select-standard"
                  color="warning"
                  sx={{
                    background: "#fff",
                    borderRadius: 1,
                    paddingLeft: 1,
                    paddingTop: 0.5,
                    marginLeft: 1,
                    fontSize: 12
                  }}
                  value={rowPerPage}
                  onChange={handleChangeRowPerPage}
                  defaultValue={5}
                  size="small"
                >
                  <MenuItem value={5}>5</MenuItem>
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={25}>25</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                </Select>
              </FormControl>
            </Stack>
          </>
        )}
      </Container>

      <OptionalWidthModal
        title="Ajouter un BU"
        // breackPoint="lg"
        onClose={handleCloseModal}
        open={openModal}
      >
        <BusinessUnitForm
          allPays={allPays}
          http={http}
          reloadList={reloadList}
          onClose={handleCloseModal}
          setOpenSnack={setOpenSnack}
          setAlerteMessage={setAlerteMessage}
          setAlertType={setAlertType}
        />
      </OptionalWidthModal>

      <div>
        <Backdrop
          sx={{ color: "white", zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open={loader}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      </div>

      {stackAlert}
    </>
  );
};

export default Bu;
